/** WCAG 2.1 AA minimum contrast for normal (non-large) text. */
export const WCAG_AA_NORMAL_TEXT_RATIO = 4.5;

/**
 * Strike color used when a tenant does not set `--comparison-strike-color`.
 * `#6B7280` (subtext) is ~4.9:1 on white but can drop below AA on tinted
 * comparison columns; gray-600 stays ≥4.5:1 on white and common neutrals.
 */
export const COMPARISON_STRIKE_COLOR = '#4B5563';

export const COMPARISON_STRIKE_SURFACES = {
  default: '#FFFFFF',
  highlighted: '#F3F4F6',
  edxNeutral50: '#F9F8F6',
} as const;

function srgbToLinear(channel: number): number {
  const s = channel / 255;
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const normalized = hex.trim().replace('#', '');
  const full =
    normalized.length === 3
      ? normalized
          .split('')
          .map((c) => c + c)
          .join('')
      : normalized;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;
  return {
    r: parseInt(full.slice(0, 2), 16),
    g: parseInt(full.slice(2, 4), 16),
    b: parseInt(full.slice(4, 6), 16),
  };
}

function relativeLuminance(hex: string): number {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0;
  return (
    0.2126 * srgbToLinear(rgb.r) +
    0.7152 * srgbToLinear(rgb.g) +
    0.0722 * srgbToLinear(rgb.b)
  );
}

/** Normalize `#rgb`, `#rrggbb`, or `rgb()`/`rgba()` to `#rrggbb`. */
export function parseCssColorToHex(color: string): string | null {
  const rgbMatch = color
    .trim()
    .match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (rgbMatch) {
    const toHex = (value: string) =>
      Number(value).toString(16).padStart(2, '0');
    return `#${toHex(rgbMatch[1])}${toHex(rgbMatch[2])}${toHex(rgbMatch[3])}`;
  }
  const rgb = hexToRgb(color);
  if (!rgb) return null;
  const toHex = (value: number) => value.toString(16).padStart(2, '0');
  return `#${toHex(rgb.r)}${toHex(rgb.g)}${toHex(rgb.b)}`;
}

/** WCAG 2.1 relative luminance contrast ratio for two hex colors. */
export function contrastRatio(foreground: string, background: string): number {
  const a = relativeLuminance(foreground);
  const b = relativeLuminance(background);
  const hi = Math.max(a, b);
  const lo = Math.min(a, b);
  return (hi + 0.05) / (lo + 0.05);
}

export function meetsWcagAaNormalText(
  foreground: string,
  background: string,
): boolean {
  return contrastRatio(foreground, background) >= WCAG_AA_NORMAL_TEXT_RATIO;
}
